/**
 * Utility functions for handling multilingual content
 */

/**
 * Gets the localized text from a multilingual content object
 * @param content - The multilingual content object
 * @param currentLocale - The current locale (e.g., 'en', 'uk', 'fr')
 * @returns The localized text in the appropriate language
 */
export const getLocalizedText = (
  content: any,
  currentLocale: string = "en"
): string => {
  // If content is already a string, return it
  if (typeof content === "string") {
    return content;
  }

  // If content is null or undefined, return empty string
  if (!content || typeof content !== "object") {
    return "";
  }

  // Normalize locale to supported language
  const locale = currentLocale.toLowerCase().slice(0, 2);

  // Try to get text in the current locale
  if (
    content[locale] &&
    typeof content[locale] === "string" &&
    content[locale].trim() !== ""
  ) {
    return content[locale];
  }

  // Fallback order: en -> uk -> fr -> first non-empty value
  const fallbackOrder = ["en", "uk", "fr"];

  for (const fallbackLang of fallbackOrder) {
    if (
      content[fallbackLang] &&
      typeof content[fallbackLang] === "string" &&
      content[fallbackLang].trim() !== ""
    ) {
      return content[fallbackLang];
    }
  }

  // If no localized text found, return the first non-empty value
  const firstNonEmpty = Object.values(content).find(
    (text) => typeof text === "string" && text.trim() !== ""
  );
  return (firstNonEmpty as string) || "";
};
